import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import {
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Loader2,
  TrendingUp,
  Sparkles,
  BookOpen,
  ArrowRight,
  Database,
  LayoutTemplate,
  FileText,
  Rocket,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils/cn';
import { useToast } from '@/lib/hooks/use-toast';
import { useAuth } from '../hooks/use-auth';
import {
  useOnboardingStore,
  type OnboardingSegment,
} from '@/features/onboarding/store/onboarding-store';
import { SEGMENTS, SEGMENT_MAP } from '@/features/onboarding/data/segments';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { COUNTRIES } from '../data/countries';

// ── Welcome Modal ───────────────────────────────────────────────

const PARTICLES = Array.from({ length: 18 }, (_, i) => ({
  id: i,
  x: Math.random() * 100,
  delay: Math.random() * 0.8,
  duration: 1.2 + Math.random() * 1.2,
  size: 6 + Math.random() * 10,
  color: ['#6366f1', '#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ec4899'][i % 6],
  rotate: Math.random() * 360,
}));

const STEPS_INFO = [
  { icon: Database, label: 'Datos maestros', color: 'text-blue-600', bg: 'bg-blue-100' },
  {
    icon: LayoutTemplate,
    label: 'Modelo de precios',
    color: 'text-violet-600',
    bg: 'bg-violet-100',
  },
  { icon: FileText, label: 'Primera cotización', color: 'text-emerald-600', bg: 'bg-emerald-100' },
];

function WelcomeModal({
  companyName,
  segment,
  onStart,
}: {
  companyName: string;
  segment: OnboardingSegment | null;
  onStart: () => void;
}) {
  const segDef = segment ? SEGMENT_MAP[segment] : null;
  const [burst, setBurst] = useState(false);

  useEffect(() => {
    // Trigger confetti burst after mount
    const t = setTimeout(() => setBurst(true), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
    >
      {/* Confetti particles */}
      <AnimatePresence>
        {burst &&
          PARTICLES.map((p) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 1, x: '50vw', y: '50vh', scale: 0, rotate: 0 }}
              animate={{
                opacity: [1, 1, 0],
                x: `calc(${p.x}vw)`,
                y: `calc(-${20 + Math.random() * 60}vh)`,
                scale: [0, 1, 0.8],
                rotate: p.rotate,
              }}
              transition={{ duration: p.duration, delay: p.delay, ease: 'easeOut' }}
              className="fixed pointer-events-none rounded-sm"
              style={{ width: p.size, height: p.size * 0.5, backgroundColor: p.color, zIndex: 51 }}
            />
          ))}
      </AnimatePresence>

      {/* Modal card */}
      <motion.div
        initial={{ opacity: 0, scale: 0.85, y: 30 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ type: 'spring', stiffness: 300, damping: 25, delay: 0.05 }}
        className="relative bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
      >
        {/* Top gradient bar */}
        <div className="h-1.5 w-full bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500" />

        {/* Decorative orbs */}
        <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-violet-400/20 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full bg-blue-400/20 blur-2xl pointer-events-none" />

        <div className="p-7">
          {/* Icon + celebration */}
          <div className="flex flex-col items-center text-center mb-6">
            <motion.div
              initial={{ scale: 0, rotate: -20 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: 'spring', stiffness: 400, damping: 18, delay: 0.2 }}
              className="relative mb-4"
            >
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-violet-500 to-blue-500 flex items-center justify-center shadow-lg shadow-violet-200">
                <Rocket className="w-10 h-10 text-white" />
              </div>
              {/* Sparkle accents */}
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
                className="absolute -top-1 -right-1"
              >
                <Sparkles className="w-5 h-5 text-yellow-400" />
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
            >
              <h2 className="text-2xl font-bold tracking-tight">¡{companyName} está listo! 🎉</h2>
              {segDef && (
                <p className="text-sm text-muted-foreground mt-1">
                  {segDef.emoji} Configurado para {segDef.title}
                </p>
              )}
            </motion.div>
          </div>

          {/* Message */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
            className="rounded-xl bg-gradient-to-br from-violet-50 to-blue-50 border border-violet-100 p-4 mb-5"
          >
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <BookOpen className="w-4 h-4 text-violet-600" />
              </div>
              <div>
                <p className="font-semibold text-sm">Te vamos a enseñar todo</p>
                <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                  En el tutorial interactivo seguirás a{' '}
                  {segDef ? `${segDef.personas.admin.name} y su equipo` : 'Carlos y su equipo'} paso
                  a paso, desde la configuración hasta crear tu primera cotización real.
                </p>
              </div>
            </div>
          </motion.div>

          {/* 3 steps */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55 }}
            className="flex gap-2 mb-6"
          >
            {STEPS_INFO.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1.5 text-center">
                  <div className={cn('w-9 h-9 rounded-xl flex items-center justify-center', s.bg)}>
                    <Icon className={cn('w-4 h-4', s.color)} />
                  </div>
                  <p className="text-[11px] font-medium leading-tight text-muted-foreground">
                    {s.label}
                  </p>
                  {i < STEPS_INFO.length - 1 && (
                    <div className="absolute" style={{ display: 'none' }} />
                  )}
                </div>
              );
            })}
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.65 }}
          >
            <Button
              onClick={onStart}
              className="w-full h-12 text-base font-semibold gap-2 bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-700 hover:to-blue-700 shadow-lg shadow-violet-200/60"
            >
              Empezar tutorial
              <ArrowRight className="w-4 h-4" />
            </Button>
            <p className="text-center text-xs text-muted-foreground mt-3">
              También puedes acceder al tutorial desde el menú en cualquier momento
            </p>
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ── Types ──────────────────────────────────────────────────────

interface FlowData {
  industrySegment: OnboardingSegment | null;
  country: string;
  companySize: string;
  companyName: string;
  taxId: string;
  companyPhone: string;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
}

const INITIAL: FlowData = {
  industrySegment: null,
  country: '',
  companySize: '',
  companyName: '',
  taxId: '',
  companyPhone: '',
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  confirmPassword: '',
};

const COMPANY_SIZES = [
  { value: '1-10', label: '1–10 personas', desc: 'Pequeño equipo o startup' },
  { value: '11-50', label: '11–50 personas', desc: 'Empresa en crecimiento' },
  { value: '51-200', label: '51–200 personas', desc: 'Mediana empresa' },
  { value: '200+', label: '200+ personas', desc: 'Gran empresa o corporativo' },
];

const TOTAL_STEPS = 5;

// ── Step slide variants ─────────────────────────────────────────

const variants = {
  enter: (dir: number) => ({ opacity: 0, x: dir > 0 ? 60 : -60 }),
  center: { opacity: 1, x: 0 },
  exit: (dir: number) => ({ opacity: 0, x: dir > 0 ? -60 : 60 }),
};

// ── Main component ─────────────────────────────────────────────

export function RegisterFlow() {
  const navigate = useNavigate();
  const { register: registerUser } = useAuth();
  const { toast } = useToast();
  const { setSegment, complete: completeOnboarding } = useOnboardingStore();

  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);
  const [data, setData] = useState<FlowData>(INITIAL);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);

  // Ref siempre apunta al data más reciente — evita closure stale en setTimeout
  const dataRef = useRef(data);
  dataRef.current = data;

  const stepRef = useRef(step);
  stepRef.current = step;

  const update = (updates: Partial<FlowData>) => {
    setData((prev) => ({ ...prev, ...updates }));
    const cleared = { ...errors };
    Object.keys(updates).forEach((k) => delete cleared[k]);
    setErrors(cleared);
  };

  const goNext = () => {
    const stepErrors = validateStep(stepRef.current, dataRef.current);
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      return;
    }
    setErrors({});
    setDir(1);
    setStep((s) => s + 1);
  };

  const goPrev = () => {
    setErrors({});
    setDir(-1);
    setStep((s) => s - 1);
  };

  const handleSubmit = async () => {
    const stepErrors = validateStep(4, dataRef.current);
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      return;
    }
    try {
      setIsLoading(true);
      await registerUser({
        companyName: data.companyName,
        taxId: data.taxId || undefined,
        companyEmail: data.email,
        companyPhone: data.companyPhone || undefined,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        password: data.password,
        confirmPassword: data.confirmPassword,
        country: data.country || undefined,
        companySize: data.companySize || undefined,
        industrySegment: data.industrySegment || undefined,
      });
      if (data.industrySegment) setSegment(data.industrySegment);
      completeOnboarding();
      useOnboardingStore.setState({ isFirstTime: true });
      setShowWelcome(true);
    } catch (error: any) {
      const msg =
        error.response?.data?.error || error.response?.data?.message || 'Error al crear la cuenta';
      toast({ title: 'Error al registrarse', description: msg, variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const progress = (step / (TOTAL_STEPS - 1)) * 100;

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-white via-blue-50/40 to-violet-50/30">
      {/* ── Welcome modal (shown after successful registration) ── */}
      <AnimatePresence>
        {showWelcome && (
          <WelcomeModal
            companyName={data.companyName}
            segment={data.industrySegment}
            onStart={() => {
              setShowWelcome(false);
              navigate('/tutorial');
            }}
          />
        )}
      </AnimatePresence>

      {/* ── Top bar ── */}
      <div className="flex items-center justify-between px-6 pt-5 pb-0 flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-primary/10 rounded-lg flex items-center justify-center">
            <TrendingUp className="w-3.5 h-3.5 text-primary" />
          </div>
          <span className="font-bold text-sm">Prixia</span>
        </div>
        <span className="text-xs text-muted-foreground tabular-nums">
          {step + 1} / {TOTAL_STEPS}
        </span>
      </div>

      {/* ── Progress bar ── */}
      <div className="px-6 pt-3 flex-shrink-0">
        <div className="h-1 bg-muted rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-primary rounded-full"
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
          />
        </div>
      </div>

      {/* ── Step content ── */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-8 overflow-hidden">
        <div className="w-full max-w-lg">
          <AnimatePresence mode="wait" custom={dir}>
            <motion.div
              key={step}
              custom={dir}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.25, ease: 'easeInOut' }}
            >
              {step === 0 && (
                <StepSegment data={data} errors={errors} update={update} onNext={goNext} />
              )}
              {step === 1 && (
                <StepCountry data={data} errors={errors} update={update} onNext={goNext} />
              )}
              {step === 2 && (
                <StepSize data={data} errors={errors} update={update} onNext={goNext} />
              )}
              {step === 3 && (
                <StepCompany data={data} errors={errors} update={update} onNext={goNext} />
              )}
              {step === 4 && (
                <StepAccount
                  data={data}
                  errors={errors}
                  update={update}
                  onSubmit={handleSubmit}
                  isLoading={isLoading}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* ── Bottom navigation ── */}
      <div className="px-6 pb-6 flex items-center justify-between flex-shrink-0">
        <div>
          {step > 0 ? (
            <button
              onClick={goPrev}
              className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              Atrás
            </button>
          ) : (
            <p className="text-sm text-muted-foreground">
              ¿Ya tienes cuenta?{' '}
              <Link to="/login" className="text-primary hover:underline font-medium">
                Iniciar sesión
              </Link>
            </p>
          )}
        </div>
        <div className="flex gap-1.5">
          {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
            <div
              key={i}
              className={cn(
                'h-1.5 rounded-full transition-all duration-300',
                i === step ? 'w-6 bg-primary' : i < step ? 'w-1.5 bg-primary/40' : 'w-1.5 bg-muted'
              )}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Step validation ─────────────────────────────────────────────

/*
  🔴 Estas dos reglas se usaban para FILTRAR lo que se tipeaba, descartando la
  tecla en silencio si no coincidía. El resultado era un campo que ignora lo que
  escribís sin decir nada: se aprieta una tecla, no aparece nada, y no hay ningún
  mensaje. Para quien lo usa, el teclado está roto.

  Lo encontró una prueba E2E de registro, que quedaba trabada en el paso 5 con
  "Apellido: Requerido" — el apellido de prueba traía dígitos y el campo los
  tragaba enteros, así que nunca se llenaba.

  Ahora solo VALIDAN, en `validateStep`, que ya mostraba "Solo letras y espacios"
  para el mismo caso. El filtro era redundante con esa validación; lo único que
  agregaba era el silencio.
*/
const NAME_REGEX = /^[a-zA-ZÀ-ÿñÑ\s.\-']*$/;
const PHONE_REGEX = /^[+\d\s\-()]*$/;

function validateStep(step: number, data: FlowData): Record<string, string> {
  const e: Record<string, string> = {};
  if (step === 0 && !data.industrySegment) e.industrySegment = 'Selecciona el tipo de negocio';
  if (step === 1 && !data.country) e.country = 'Selecciona tu país';
  if (step === 3) {
    if (!data.companyName.trim()) e.companyName = 'El nombre de la empresa es requerido';
    else if (data.companyName.trim().length < 2) e.companyName = 'Mínimo 2 caracteres';
    else if (data.companyName.trim().length > 100) e.companyName = 'Máximo 100 caracteres';

    // Tax ID (opcional pero si se llena, mínimo 5)
    if (data.taxId.trim() && data.taxId.trim().length < 5) e.taxId = 'Mínimo 5 caracteres';

    // Teléfono (opcional pero si se llena, mínimo 7 dígitos y formato válido)
    if (data.companyPhone.trim()) {
      if (!PHONE_REGEX.test(data.companyPhone))
        e.companyPhone = 'Solo números, +, espacios y guiones';
      else if (data.companyPhone.replace(/[^\d]/g, '').length < 7)
        e.companyPhone = 'Mínimo 7 dígitos';
    }
  }
  if (step === 4) {
    if (!data.firstName.trim()) e.firstName = 'Requerido';
    else if (data.firstName.trim().length < 2) e.firstName = 'Mínimo 2 caracteres';
    else if (!NAME_REGEX.test(data.firstName)) e.firstName = 'Solo letras y espacios';

    if (!data.lastName.trim()) e.lastName = 'Requerido';
    else if (data.lastName.trim().length < 2) e.lastName = 'Mínimo 2 caracteres';
    else if (!NAME_REGEX.test(data.lastName)) e.lastName = 'Solo letras y espacios';

    if (!data.email.trim()) e.email = 'Requerido';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) e.email = 'Email inválido';
    if (!data.password) e.password = 'Requerido';
    else if (data.password.length < 8) e.password = 'Mínimo 8 caracteres';
    if (!data.confirmPassword) e.confirmPassword = 'Requerido';
    else if (data.password !== data.confirmPassword) e.confirmPassword = 'No coinciden';
  }
  return e;
}

// ── Step props ─────────────────────────────────────────────────

interface StepProps {
  data: FlowData;
  errors: Record<string, string>;
  update: (u: Partial<FlowData>) => void;
}

// ── Step 1: Tipo de negocio ─────────────────────────────────────

function StepSegment({ data, errors, update, onNext }: StepProps & { onNext: () => void }) {
  return (
    <div className="space-y-8">
      <StepHeader
        step={1}
        title="¿Qué tipo de negocio tienes?"
        subtitle="Personalizamos la plataforma para tu industria"
      />
      <div className="grid grid-cols-2 gap-3">
        {SEGMENTS.map((seg) => {
          const isSelected = data.industrySegment === seg.id;
          return (
            <button
              key={seg.id}
              type="button"
              onClick={() => {
                update({ industrySegment: seg.id });
                setTimeout(onNext, 200); // auto-avanza después de seleccionar
              }}
              className={cn(
                'relative text-left p-4 rounded-2xl border-2 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]',
                isSelected
                  ? `${seg.borderClass} ${seg.bgClass} ring-2 ${seg.ringClass}`
                  : 'border-border bg-background hover:border-muted-foreground/30 hover:bg-muted/30'
              )}
            >
              {isSelected && (
                <CheckCircle2 className={cn('absolute top-3 right-3 w-4 h-4', seg.textClass)} />
              )}
              <div className="space-y-1.5">
                <span className="text-3xl">{seg.emoji}</span>
                <div>
                  <p className={cn('text-sm font-semibold', isSelected && seg.textClass)}>
                    {seg.title}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                    {seg.subtitle}
                  </p>
                </div>
                <div className="flex flex-wrap gap-1 pt-1">
                  {seg.examples.slice(0, 2).map((ex) => (
                    <span
                      key={ex}
                      className={cn(
                        'text-[10px] px-2 py-0.5 rounded-full border',
                        isSelected
                          ? `${seg.borderClass} ${seg.textClass} bg-transparent`
                          : 'border-muted text-muted-foreground'
                      )}
                    >
                      {ex}
                    </span>
                  ))}
                </div>
              </div>
            </button>
          );
        })}
      </div>
      {errors.industrySegment && (
        <p className="text-xs text-destructive text-center">{errors.industrySegment}</p>
      )}
    </div>
  );
}

// ── Step 2: País ────────────────────────────────────────────────

function StepCountry({ data, errors, update, onNext }: StepProps & { onNext: () => void }) {
  return (
    <div className="space-y-8">
      <StepHeader
        step={2}
        title="¿En qué país operas?"
        subtitle="Adaptamos monedas, impuestos y regulaciones a tu mercado"
      />
      <div className="space-y-3">
        <SearchableSelect
          options={COUNTRIES}
          value={data.country}
          onValueChange={(val) => {
            update({ country: val });
            setTimeout(onNext, 200);
          }}
          placeholder="Busca tu país..."
          searchPlaceholder="Escribe para buscar..."
          className="h-12 text-base"
        />
        {errors.country && <p className="text-xs text-destructive">{errors.country}</p>}
      </div>
      <ContinueButton onClick={onNext} />
    </div>
  );
}

// ── Step 3: Tamaño ──────────────────────────────────────────────

function StepSize({ data, update, onNext }: StepProps & { onNext: () => void }) {
  return (
    <div className="space-y-8">
      <StepHeader
        step={3}
        title="¿Cuántas personas trabajan en tu empresa?"
        subtitle="Opcional — nos ayuda a configurar los límites de aprobación"
      />
      <div className="grid grid-cols-2 gap-3">
        {COMPANY_SIZES.map((size) => {
          const isSelected = data.companySize === size.value;
          return (
            <button
              key={size.value}
              type="button"
              onClick={() => {
                update({ companySize: size.value });
                setTimeout(onNext, 200);
              }}
              className={cn(
                'text-left p-4 rounded-2xl border-2 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]',
                isSelected
                  ? 'border-primary bg-primary/8 ring-2 ring-primary/20'
                  : 'border-border bg-background hover:border-muted-foreground/30 hover:bg-muted/30'
              )}
            >
              <p className={cn('text-sm font-semibold', isSelected && 'text-primary')}>
                {size.label}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">{size.desc}</p>
            </button>
          );
        })}
      </div>
      <button
        onClick={onNext}
        className="text-sm text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors block mx-auto"
      >
        Saltar este paso
      </button>
    </div>
  );
}

// ── Step 4: Datos empresa ────────────────────────────────────────

function StepCompany({ data, errors, update, onNext }: StepProps & { onNext: () => void }) {
  return (
    <div className="space-y-8">
      <StepHeader
        step={4}
        title="Cuéntanos sobre tu empresa"
        subtitle="Esta información aparecerá en tus cotizaciones y documentos"
      />
      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="companyName" className="text-sm font-medium">
            Nombre de la empresa <span className="text-destructive">*</span>
          </Label>
          <Input
            id="companyName"
            placeholder="Mi Empresa S.A."
            value={data.companyName}
            onChange={(e) => update({ companyName: e.target.value })}
            error={errors.companyName}
            autoFocus
            className="h-11"
            onKeyDown={(e) => e.key === 'Enter' && onNext()}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="taxId" className="text-sm font-medium">
              NIT / RUT / Tax ID{' '}
              <span className="text-muted-foreground font-normal text-xs">(opcional)</span>
            </Label>
            <Input
              id="taxId"
              placeholder="123456789"
              value={data.taxId}
              onChange={(e) => update({ taxId: e.target.value })}
              error={errors.taxId}
              className="h-11"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="companyPhone" className="text-sm font-medium">
              Teléfono <span className="text-muted-foreground font-normal text-xs">(opcional)</span>
            </Label>
            <Input
              id="companyPhone"
              placeholder="+591 7 000 0000"
              value={data.companyPhone}
              onChange={(e) => {
                update({ companyPhone: e.target.value });
              }}
              error={errors.companyPhone}
              className="h-11"
            />
          </div>
        </div>
      </div>
      <ContinueButton onClick={onNext} />
    </div>
  );
}

// ── Step 5: Datos cuenta ─────────────────────────────────────────

function StepAccount({
  data,
  errors,
  update,
  onSubmit,
  isLoading,
}: StepProps & { onSubmit: () => void; isLoading: boolean }) {
  return (
    <div className="space-y-8">
      <StepHeader
        step={5}
        title="Crea tu cuenta de acceso"
        subtitle="Serás el administrador principal de tu organización"
      />
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="firstName" className="text-sm font-medium">
              Nombre <span className="text-destructive">*</span>
            </Label>
            <Input
              id="firstName"
              placeholder="Juan"
              value={data.firstName}
              onChange={(e) => {
                update({ firstName: e.target.value });
              }}
              error={errors.firstName}
              autoFocus
              className="h-11"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="lastName" className="text-sm font-medium">
              Apellido <span className="text-destructive">*</span>
            </Label>
            <Input
              id="lastName"
              placeholder="Pérez"
              value={data.lastName}
              onChange={(e) => {
                update({ lastName: e.target.value });
              }}
              error={errors.lastName}
              className="h-11"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="email" className="text-sm font-medium">
            Email de acceso <span className="text-destructive">*</span>
          </Label>
          <Input
            id="email"
            type="email"
            placeholder="juan.perez@miempresa.com"
            value={data.email}
            onChange={(e) => update({ email: e.target.value })}
            error={errors.email}
            className="h-11"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="password" className="text-sm font-medium">
              Contraseña <span className="text-destructive">*</span>
            </Label>
            <Input
              id="password"
              type="password"
              placeholder="••••••••"
              value={data.password}
              onChange={(e) => update({ password: e.target.value })}
              error={errors.password}
              className="h-11"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="confirmPassword" className="text-sm font-medium">
              Confirmar <span className="text-destructive">*</span>
            </Label>
            <Input
              id="confirmPassword"
              type="password"
              placeholder="••••••••"
              value={data.confirmPassword}
              onChange={(e) => update({ confirmPassword: e.target.value })}
              error={errors.confirmPassword}
              className="h-11"
            />
          </div>
        </div>

        <p className="text-xs text-muted-foreground">
          Al registrarte aceptas los{' '}
          <Link to="/terms" target="_blank" className="text-primary hover:underline">
            Términos de uso
          </Link>{' '}
          y la{' '}
          <Link to="/privacy" target="_blank" className="text-primary hover:underline">
            Política de privacidad
          </Link>
          .
        </p>
      </div>

      <Button onClick={onSubmit} size="lg" className="w-full h-12 text-base" disabled={isLoading}>
        {isLoading ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Creando tu cuenta...
          </>
        ) : (
          <>
            <CheckCircle2 className="w-4 h-4 mr-2" />
            Crear cuenta gratis
          </>
        )}
      </Button>
    </div>
  );
}

// ── Shared UI pieces ────────────────────────────────────────────

function StepHeader({ step, title, subtitle }: { step: number; title: string; subtitle: string }) {
  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold uppercase tracking-widest text-primary">Paso {step}</p>
      <h1 className="text-2xl sm:text-3xl font-bold leading-tight">{title}</h1>
      <p className="text-muted-foreground text-sm sm:text-base">{subtitle}</p>
    </div>
  );
}

function ContinueButton({ onClick }: { onClick: () => void }) {
  return (
    <Button onClick={onClick} size="lg" className="w-full h-12 text-base gap-2">
      Continuar
      <ChevronRight className="w-4 h-4" />
    </Button>
  );
}
